
import React, { useEffect } from 'react';
import { ArrowLeft, Edit, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { invalidateImageCache } from '@/utils/serviceWorkerUtils';
import { getTransformationUrl } from '@/utils/imageTransformationUtils';

interface ServiceDetailsHeaderProps {
  navigate: (path: string | number) => void;
  id?: string;
  service: any;
  isAdmin: boolean;
  isServiceOwner: boolean;
  canEdit: boolean;
}

export const ServiceDetailsHeader: React.FC<ServiceDetailsHeaderProps> = ({
  navigate,
  id,
  service,
  isAdmin,
  isServiceOwner,
  canEdit
}) => {
  // Function to determine if user can edit the service
  const userCanEdit = isAdmin || (isServiceOwner && canEdit);

  // Check if the image should be refreshed in service worker cache
  useEffect(() => {
    if (service?.image_url && navigator.serviceWorker?.controller) {
      // Tell service worker to refresh this image
      invalidateImageCache(service.image_url);
    }
    
    // Also check for images in the service_images relationship
    if (service?.service_images && service.service_images.length > 0) {
      const indexImage = service.service_images.find((img: any) => img.is_index_image);
      const firstImage = service.service_images[0];
      
      if ((indexImage?.bucket && indexImage?.original_path) || 
          (firstImage?.bucket && firstImage?.original_path)) {
        const image = indexImage || firstImage;
        try {
          const { data } = supabase.storage
            .from(image.bucket)
            .getPublicUrl(image.original_path);
          
          if (data?.publicUrl && navigator.serviceWorker?.controller) {
            invalidateImageCache(data.publicUrl);
            console.log('Invalidated cache for image:', data.publicUrl);
          }
        } catch (error) {
          console.error('Error getting public URL:', error);
        }
      }
    }
  }, [service]);

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2 bg-white p-4 rounded-lg shadow-sm">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold truncate max-w-[200px] sm:max-w-[400px]">
            {service?.name || 'Service Details'}
          </h1>
        </div>
        {service?.rating !== null && (
          <div className="flex items-center gap-1 text-sm">
            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
            <span>{service.rating?.toFixed(1) || '0.0'}</span>
          </div>
        )}
      </div>
      
      {userCanEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/services/edit/${id}`)}
          className="flex items-center gap-2"
        >
          <Edit className="h-4 w-4" />
          Edit
        </Button>
      )}
    </div>
  );
};
